/**
 * The turn in progress, rendered as the next chapter of the feed.
 *
 * This is the component that makes the fifty seconds into a design surface instead of a
 * spinner. It mounts the instant the player submits and it is *already* a chapter: their
 * action is echoed at the top in their own voice, the ticker under it fills with `change`
 * events through the whole silent resolution phase, and the narration writes itself into the
 * space between them when Phase B starts. Nothing overlays, nothing greys out, and the
 * twenty-four chapters above it stay readable the entire time.
 *
 * It renders through `Scene` — the same shell as a committed chapter — so that the moment the
 * `chapter` event lands and `commitTurn` swaps this out for a real `ChapterScene`, nothing
 * moves. That swap is the frame most likely to look like a bug, and the fix for it is
 * structural rather than cosmetic: there is one layout, and both callers use it.
 *
 * The one honest difference is the dateline. A live turn has a chapter number and nothing
 * else — the day, the hour and the place are the *result* of the turn, and the server
 * denormalizes them onto the row at commit (U4). Guessing them here would be inventing state,
 * so the header is one segment until it is three.
 */
import type { RefObject } from 'react';
import { ChangeTicker } from '@/features/story/ChangeTicker';
import { Prose } from '@/features/story/Prose';
import { Scene } from '@/features/story/Scene';
import { useStickToBottom } from '@/features/story/useStickToBottom';
import { useFrameBuffer } from '@/features/turn/useFrameBuffer';
import type { Change } from '@/api/generated';
import { useT } from '@/i18n/context';

interface LiveSceneProps {
  /** From `turn.started`. Null for the moment before the first event arrives. */
  chapterNumber: number | null;
  /** What the player submitted, echoed from the instant they submit it. */
  action: string | null;
  /** Everything `narration.delta` has appended so far. */
  narration: string;
  /** Every `change` so far, in arrival order — most of them from before the prose existed. */
  changes: readonly Change[];
  /**
   * Where the reader was, recorded by `useReaderPinning` on the story screen.
   *
   * A prop rather than a hook call in here, and the awkwardness is the point: the answer has to
   * be taken *before* this component exists, because this component's own arrival pushes the
   * bottom of the document ~256px away from whoever was sitting at it. Sampling it here is what
   * made the feed follow nobody.
   */
  pinned: RefObject<boolean>;
}

export function LiveScene({ chapterNumber, action, narration, changes, pinned }: LiveSceneProps) {
  const t = useT();
  const text = useFrameBuffer(narration);

  // Follow the text down the page, but only for a reader who is at the bottom of it. Both
  // inputs matter: during Phase A the ticker is the only thing growing — and it is `text`
  // rather than `narration`, because the rendered length is what says the DOM has grown.
  useStickToBottom(pinned, `${String(text.length)}:${String(changes.length)}`, true);

  return (
    <Scene
      dateline={[
        chapterNumber === null
          ? t('ui.turn.next_chapter')
          : t('ui.chapter.number', { chapter: chapterNumber }),
      ]}
      action={action}
      footer={<ChangeTicker changes={changes} live />}
    >
      <Prose text={text} trailing={<Cursor />} />
    </Scene>
  );
}

/**
 * The cursor sits inside the last paragraph via `Prose`'s `trailing` slot, so it rides the end
 * of the text through every reflow instead of being positioned against it.
 *
 * It is the only thing in the feed that blinks, and `index.css`'s global reduced-motion floor
 * stops it doing that for anyone who has asked it not to — leaving a solid block, which still
 * reads as "more is coming".
 */
function Cursor() {
  return (
    <span
      aria-hidden="true"
      className="bg-accent ml-0.5 inline-block h-[0.95em] w-[0.5ch] translate-y-[0.1em] animate-[cursor-blink_1.1s_steps(2,start)_infinite]"
    />
  );
}
